import { ActivityVariable } from "../types/activity";
import { isTimeDurationVariable } from "./timeDurationUtils";

/** A displayable number with its unit kept apart so it can be styled lighter. */
export interface ResultValue {
  number: string;
  unit?: string;
}

const SECONDS_PER_TIME_UNIT: Record<string, number> = {
  s: 1, sec: 1, secs: 1, second: 1, seconds: 1,
  min: 60, mins: 60, minute: 60, minutes: 60,
  h: 3600, hr: 3600, hrs: 3600, hour: 3600, hours: 3600,
  "mm:ss": 1, "hh:mm:ss": 1, "mm:ss.ss": 1,
};

/** Up to two decimals, trailing zeros dropped: 100, 6.1, 6.12. */
export function trimmedNumber(value: number): string {
  if (Number.isInteger(value)) return String(value);
  return value.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
}

/**
 * Times always show two decimals: "11.40 s", "2:01.34", "1:05:03.50"; a
 * minute or longer reads as a clock with no unit. Matches the native apps.
 */
export function timeValue(seconds: number): ResultValue {
  const totalCs = Math.round(Math.max(0, seconds) * 100);
  const hours = Math.floor(totalCs / 360_000);
  const minutes = Math.floor((totalCs % 360_000) / 6_000);
  const secs = Math.floor((totalCs % 6_000) / 100);
  const cs = totalCs % 100;
  const pad2 = (n: number) => String(n).padStart(2, "0");
  if (hours > 0) return { number: `${hours}:${pad2(minutes)}:${pad2(secs)}.${pad2(cs)}` };
  if (minutes > 0) return { number: `${minutes}:${pad2(secs)}.${pad2(cs)}` };
  return { number: `${secs}.${pad2(cs)}`, unit: "s" };
}

export function resultValue(variable: ActivityVariable): ResultValue | null {
  const { measurement } = variable;
  if (measurement === null || measurement === undefined || Number.isNaN(measurement)) return null;
  const unit = (variable.unit ?? "").trim();
  const lower = unit.toLowerCase();
  const name = (variable.name ?? "").trim().toLowerCase();
  // Time is stored as raw seconds. Its activity unit (often "min") is not a
  // scale for that number. Multiplying by the unit turned 12.34s into 12:20.40.
  // Rest is stored in its unit, so the unit still converts it back to seconds.
  if (name === "time") return timeValue(measurement);
  if (lower in SECONDS_PER_TIME_UNIT) return timeValue(measurement * SECONDS_PER_TIME_UNIT[lower]);
  if (!unit && isTimeDurationVariable(variable.name)) return timeValue(measurement);
  if (lower === "ms") return { number: measurement.toFixed(2), unit };
  return { number: trimmedNumber(measurement), unit: unit || undefined };
}
